// src/pages/volunteering/formList.js
import React, { useCallback, useEffect, useMemo, useReducer, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Card, Collapse, Col, Dropdown, Row, Tag, Tooltip } from "antd";
import { FaEye, FaEdit, FaTrash, FaLockOpen, FaLock, FaChevronDown, FaCalendarAlt, FaListUl, FaCopy } from "react-icons/fa";
import axios from "../../services/axiosConfig";
import API_ENDPOINTS from "../../services/apiEndpoints";
import { getAuthToken } from "../../auth/auth";
import useNotification from "../../hooks/useNotification";
import Layout from "../../components/layout/layout";
import TableLayout from "../../components/tableLayout/tableLayout";
import TopToolBar from "../../components/topToolBar/topToolBar";
import ConfirmModal from "../../components/confirmModal/confirmModal";
import { getAllForms, getTemplates, deleteForm } from "./api/forms";
import "./volunteering.css";

const { Panel } = Collapse;

// ─── Debounce utility ────────────────────────────────────────────────────────
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// ─── Reducer ─────────────────────────────────────────────────────────────────
const initialState = {
  forms: [],
  templates: [],
  isLoadingForms: true,
  isLoadingTemplates: true,
  searchTerm: "",
  filters: {},
  sortConfig: { key: "createdAt", direction: "desc" },
  showDeleteModal: false,
  deleteTargetId: null,
  deleteTargetType: "form", // "form" | "template"
  isDeleting: false,
  showBlockModal: false,
  blockTargetId: null,
  isBlocking: false,
  blockTargetStatus: null
};

const reducer = (state, action) => {
  switch (action.type) {
    case "setForms":
      return { ...state, forms: action.payload };
    case "setTemplates":
      return { ...state, templates: action.payload };
    case "setLoadingForms":
      return { ...state, isLoadingForms: action.payload };
    case "setLoadingTemplates":
      return { ...state, isLoadingTemplates: action.payload };
    case "setSearchTerm":
      return { ...state, searchTerm: action.payload };
    case "setFilters":
      return { ...state, filters: action.payload };
    case "updateFormBlock":
      return {
        ...state,
        forms: state.forms.map((f) => (f.formId === action.payload.formId ? { ...f, isBlocked: action.payload.isBlocked } : f))
      };
    case "removeForm":
      return {
        ...state,
        forms: state.forms.filter((f) => f.formId !== action.payload)
      };
    case "removeTemplate":
      return {
        ...state,
        templates: state.templates.filter((t) => t.formId !== action.payload)
      };
    case "setDeleteModal":
      return {
        ...state,
        showDeleteModal: action.payload.show,
        deleteTargetId: action.payload.formId || null,
        deleteTargetType: action.payload.targetType || "form"
      };
    case "setIsDeleting":
      return { ...state, isDeleting: action.payload };
    case "setBlockModal":
      return {
        ...state,
        showBlockModal: action.payload.show,
        blockTargetId: action.payload.formId || null,
        blockTargetStatus: action.payload.isBlocked ?? null
      };
    case "setIsBlocking":
      return { ...state, isBlocking: action.payload };
    case "resetFilters":
      return { ...state, filters: {}, searchTerm: "" };
    default:
      return state;
  }
};

// ─── Helpers ─────────────────────────────────────────────────────────────────
const formatDate = (iso) => {
  if (!iso) return "-";
  return new Date(iso)
    .toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    })
    .replace(/ /g, "-");
};

const getFieldCount = (form) => form.formSchema?.fields?.length ?? "-";

const FORM_TYPE_LABELS = {
  internal: "Internal",
  external: "External"
};

const FORM_PLACEHOLDERS = Array.from({ length: 6 });
const TEMPLATE_PLACEHOLDERS = Array.from({ length: 3 });

// ─── Component ───────────────────────────────────────────────────────────────
function FormList() {
  const { notification, showNotification, hideNotification } = useNotification();
  const [state, dispatch] = useReducer(reducer, initialState);
  const navigate = useNavigate();

  const [displaySearchTerm, setDisplaySearchTerm] = useState("");

  const {
    forms,
    templates,
    isLoadingForms,
    isLoadingTemplates,
    searchTerm,
    filters,
    sortConfig,
    showDeleteModal,
    deleteTargetId,
    isDeleting,
    showBlockModal,
    blockTargetId,
    isBlocking,
    blockTargetStatus
  } = state;

  const isLoading = isLoadingForms || isLoadingTemplates;

  // ── Filters config ────────────────────────────────────────────────────────
  const filtersConfig = useMemo(
    () => ({
      formType: {
        label: "Form Type",
        options: [
          { value: "internal", label: "Internal" },
          { value: "external", label: "External" }
        ]
      },
      status: {
        label: "Status",
        options: [
          { value: "active", label: "Active" },
          { value: "blocked", label: "Blocked" }
        ]
      }
    }),
    []
  );

  // ── Filtered + sorted forms ───────────────────────────────────────────────
  const filteredForms = useMemo(() => {
    let result = [...forms];

    if (filters.formType) {
      result = result.filter((f) => f.formType === filters.formType);
    }

    if (filters.status) {
      result = result.filter((f) => (filters.status === "blocked" ? f.isBlocked : !f.isBlocked));
    }

    if (searchTerm?.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter((f) => [f.title, f.description, f.formType].some((field) => (field || "").toLowerCase().includes(term)));
    }

    result.sort((a, b) => {
      const dateA = new Date(a.createdAt || 0);
      const dateB = new Date(b.createdAt || 0);
      return sortConfig.direction === "desc" ? dateB - dateA : dateA - dateB;
    });

    return result;
  }, [forms, filters, searchTerm, sortConfig]);

  // ── Debounced search ──────────────────────────────────────────────────────
  const debouncedDispatch = useMemo(() => debounce((term) => dispatch({ type: "setSearchTerm", payload: term }), 300), []);

  const handleSearch = useCallback(
    (term) => {
      setDisplaySearchTerm(term);
      debouncedDispatch(term);
    },
    [debouncedDispatch]
  );

  const handleFilterChange = useCallback((newFilters) => {
    dispatch({ type: "setFilters", payload: newFilters });
  }, []);

  const handleReset = useCallback(() => {
    dispatch({ type: "resetFilters" });
    setDisplaySearchTerm("");
  }, []);

  // ── Data loading ──────────────────────────────────────────────────────────
  const loadForms = useCallback(async () => {
    dispatch({ type: "setLoadingForms", payload: true });
    try {
      const data = await getAllForms();
      dispatch({ type: "setForms", payload: data });
    } catch (err) {
      console.error(err);
      showNotification("Failed to load forms", "error");
    } finally {
      dispatch({ type: "setLoadingForms", payload: false });
    }
  }, [showNotification]);

  const loadTemplates = useCallback(async () => {
    dispatch({ type: "setLoadingTemplates", payload: true });
    try {
      const data = await getTemplates();
      dispatch({ type: "setTemplates", payload: data });
    } catch (err) {
      console.error(err);
      showNotification("Failed to load templates", "error");
    } finally {
      dispatch({ type: "setLoadingTemplates", payload: false });
    }
  }, [showNotification]);

  useEffect(() => {
    loadForms();
    loadTemplates();
  }, [loadForms, loadTemplates]);

  // ── Block / Unblock ───────────────────────────────────────────────────────
  const toggleBlock = useCallback(
    async (formId, isBlocked) => {
      try {
        const token = await getAuthToken();
        await axios.put(
          `${API_ENDPOINTS.VOLUNTEERING_FORMS}/forms/block`,
          { formId, isBlocked },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`
            }
          }
        );
        dispatch({ type: "updateFormBlock", payload: { formId, isBlocked } });
        showNotification(isBlocked ? "Form blocked successfully" : "Form unblocked successfully", isBlocked ? "warning" : "success");
      } catch (err) {
        console.error(err);
        showNotification("Failed to update form status", "error");
      }
    },
    [showNotification]
  );

  // ── Delete ────────────────────────────────────────────────────────────────
  const openDeleteModal = useCallback((formId, e) => {
    e.stopPropagation();
    dispatch({
      type: "setDeleteModal",
      payload: { show: true, formId, targetType: "form" }
    });
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!deleteTargetId) return;
    dispatch({ type: "setIsDeleting", payload: true });
    try {
      await deleteForm(deleteTargetId);
      if (state.deleteTargetType === "template") {
        dispatch({ type: "removeTemplate", payload: deleteTargetId });
        showNotification("Template deleted successfully", "success");
      } else {
        dispatch({ type: "removeForm", payload: deleteTargetId });
        showNotification("Form deleted successfully", "success");
      }
      dispatch({ type: "setDeleteModal", payload: { show: false } });
    } catch (err) {
      console.error(err);
      showNotification(`Failed to delete ${state.deleteTargetType}`, "error");
    } finally {
      dispatch({ type: "setIsDeleting", payload: false });
    }
  }, [deleteTargetId, state.deleteTargetType, showNotification]);

  const confirmBlock = useCallback(async () => {
    if (!blockTargetId) return;
    dispatch({ type: "setIsBlocking", payload: true });
    try {
      await toggleBlock(blockTargetId, blockTargetStatus);
      dispatch({ type: "setBlockModal", payload: { show: false } });
    } finally {
      dispatch({ type: "setIsBlocking", payload: false });
    }
  }, [blockTargetId, blockTargetStatus, toggleBlock]);

  // ── Menus ─────────────────────────────────────────────────────────────────
  const createFormMenu = useMemo(
    () => ({
      items: [
        {
          key: "internal",
          label: "New Form",
          onClick: () =>
            navigate("/volunteering/createForm", {
              state: { formType: "internal" }
            })
        },
        {
          key: "external",
          label: "New External Form",
          onClick: () =>
            navigate("/volunteering/createForm", {
              state: { formType: "external" }
            })
        }
      ]
    }),
    [navigate]
  );

  // ── Excel export ──────────────────────────────────────────────────────────
  const prepareExcelData = useCallback(
    () =>
      filteredForms.map((form, index) => ({
        "SL. NO.": index + 1,
        Title: form.title,
        Description: form.description,
        "Form Type": FORM_TYPE_LABELS[form.formType] ?? form.formType,
        "No. of Fields": getFieldCount(form),
        Status: form.isBlocked ? "Blocked" : "Active",
        "Created On": formatDate(form.createdAt)
      })),
    [filteredForms]
  );

  // ── Card renderers ────────────────────────────────────────────────────────
  const renderFormCard = useCallback(
    (form) => (
      <Col xs={24} sm={12} lg={8} key={form.formId}>
        <Card
          hoverable
          className={`vfl-card ${form.isBlocked ? "vfl-card--blocked" : ""}`}
          title={form.title}
          extra={
            <div className="vfl-card-tags">
              <Tag color="blue">{FORM_TYPE_LABELS[form.formType] ?? form.formType}</Tag>
              <Tag color={form.isBlocked ? "red" : "green"}>{form.isBlocked ? "Blocked" : "Active"}</Tag>
            </div>
          }
        >
          <p className="vfl-card-description" title={form.description}>
            {form.description || "No description provided."}
          </p>

          <div className="vfl-card-meta">
            <span className="vfl-card-meta-item">
              <FaCalendarAlt className="vfl-card-meta-icon" />
              <span>Created on: </span>
              {formatDate(form.createdAt)}
            </span>
            {form.formType === "internal" && (
              <span className="vfl-card-meta-item">
                <FaListUl className="vfl-card-meta-icon" />
                {getFieldCount(form)} fields
              </span>
            )}
          </div>

          <div className="vfl-card-actions">
            {/* For internal forms: navigate to submissions page.
                For external forms: open the external URL in a new tab.
                The button style is identical in both cases. */}
            {form.formType === "internal" ? (
              <Tooltip title="View Submissions">
                <button className="vfl-table-view-btn" onClick={() => navigate(`/volunteering/formSubmissions/${form.formId}`)}>
                  <FaEye />
                </button>
              </Tooltip>
            ) : (
              <Tooltip title="View Form">
                <button className="vfl-table-view-btn" onClick={() => window.open(form.externalFormUrl, "_blank", "noopener,noreferrer")}>
                  <FaEye />
                </button>
              </Tooltip>
            )}
            <Tooltip title="Edit Form">
              <button className="vfl-table-edit-btn" onClick={() => navigate(`/volunteering/editForm/${form.formId}`)}>
                <FaEdit />
              </button>
            </Tooltip>
            <Tooltip title="Delete Form">
              <button className="vfl-table-delete-btn" onClick={(e) => openDeleteModal(form.formId, e)}>
                <FaTrash />
              </button>
            </Tooltip>
            <Tooltip title={form.isBlocked ? "Unblock Form" : "Block Form"}>
              <button
                className="vfl-table-block-btn"
                onClick={() =>
                  dispatch({
                    type: "setBlockModal",
                    payload: {
                      show: true,
                      formId: form.formId,
                      isBlocked: !form.isBlocked
                    }
                  })
                }
              >
                {form.isBlocked ? <FaLockOpen /> : <FaLock />}
              </button>
            </Tooltip>
          </div>
        </Card>
      </Col>
    ),
    [navigate, openDeleteModal]
  );

  const renderTemplateCard = useCallback(
    (template) => (
      <Col xs={24} sm={12} lg={8} key={template.formId}>
        <Card hoverable className="vfl-card" title={template.templateName || template.title} extra={<Tag color="purple">Template</Tag>}>
          <p className="vfl-card-description">{template.description || "No description provided."}</p>

          <div className="vfl-card-meta">
            <span className="vfl-card-meta-item">
              <FaCalendarAlt className="vfl-card-meta-icon" />
              <span>Created on: </span>
              {formatDate(template.createdAt)}
            </span>
            <span className="vfl-card-meta-item">
              <FaListUl className="vfl-card-meta-icon" />
              {getFieldCount(template)} fields
            </span>
          </div>

          <div className="vfl-card-actions">
            <Tooltip title="Use Template">
              <button
                className="vfl-table-use-btn"
                onClick={() =>
                  navigate("/volunteering/createForm", {
                    state: { formType: template.formType, template }
                  })
                }
              >
                <FaCopy />
              </button>
            </Tooltip>
            <Tooltip title="Edit Template">
              <button className="vfl-table-edit-btn" onClick={() => navigate(`/volunteering/editForm/${template.formId}`)}>
                <FaEdit />
              </button>
            </Tooltip>
            <Tooltip title="Delete Template">
              <button
                className="vfl-table-delete-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  dispatch({
                    type: "setDeleteModal",
                    payload: {
                      show: true,
                      formId: template.formId,
                      targetType: "template"
                    }
                  });
                }}
              >
                <FaTrash />
              </button>
            </Tooltip>
          </div>
        </Card>
      </Col>
    ),
    [navigate]
  );

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <Layout
      navTitle="Volunteer Forms"
      notificationProps={[
        {
          show: notification.show,
          message: notification.message,
          type: notification.type,
          onClose: hideNotification,
          duration: 4000
        }
      ]}
    >
      {/* ── Collapsible Templates ── */}
      <div className="vfl-collapse-section">
        <Collapse
          expandIcon={({ isActive }) => <FaChevronDown className={`vfl-collapse-icon ${isActive ? "vfl-collapse-icon--open" : ""}`} />}
          className="vfl-collapse"
        >
          <Panel header="Templates" key="templates">
            <Row gutter={[24, 24]}>
              {isLoading ? (
                TEMPLATE_PLACEHOLDERS.map((_, i) => (
                  <Col xs={24} sm={12} lg={8} key={`tpl-skeleton-${i}`}>
                    <Card loading className="vfl-card" />
                  </Col>
                ))
              ) : templates.length > 0 ? (
                templates.map(renderTemplateCard)
              ) : (
                <Col span={24}>
                  <div className="vfl-empty">
                    <p>No templates available.</p>
                  </div>
                </Col>
              )}
            </Row>
          </Panel>
        </Collapse>
      </div>

      {/* ── Forms grid ── */}
      <TableLayout
        toolbar={
          <TopToolBar
            filtersConfig={filtersConfig}
            searchPlaceholder="Search forms..."
            searchTerm={displaySearchTerm}
            filters={filters}
            onSearch={handleSearch}
            onFilterChange={handleFilterChange}
            onReset={handleReset}
            showExcelDownload={true}
            excelData={prepareExcelData()}
            excelFilename="volunteer-forms"
            excelSheetName="Forms"
            extraButtons={[
              <Dropdown menu={createFormMenu} placement="bottomLeft" key="create">
                <Button className="vfl-create-btn">Create Form</Button>
              </Dropdown>
            ]}
          />
        }
      >
        <h2 className="vfl-forms-card-title">Forms</h2>
        <Row gutter={[24, 24]} className="vfl-forms-grid">
          {isLoading ? (
            FORM_PLACEHOLDERS.map((_, i) => (
              <Col xs={24} sm={12} lg={8} key={`form-skeleton-${i}`}>
                <Card loading className="vfl-card" />
              </Col>
            ))
          ) : filteredForms.length > 0 ? (
            filteredForms.map(renderFormCard)
          ) : (
            <Col span={24}>
              <div className="vfl-empty">
                <p>No forms created yet.</p>
              </div>
            </Col>
          )}
        </Row>
      </TableLayout>

      {/* ── Delete Modal ── */}
      <ConfirmModal
        open={showDeleteModal}
        action="delete"
        entity={state.deleteTargetType}
        loading={isDeleting}
        onConfirm={confirmDelete}
        onCancel={() => dispatch({ type: "setDeleteModal", payload: { show: false } })}
      />

      {/* ── Block Modal ── */}
      <ConfirmModal
        open={showBlockModal}
        action={state.blockTargetStatus ? "block" : "unblock"}
        entity="form"
        loading={isBlocking}
        onConfirm={confirmBlock}
        onCancel={() => dispatch({ type: "setBlockModal", payload: { show: false } })}
      />
    </Layout>
  );
}

export default FormList;
